import React, { PropsWithChildren, HTMLAttributes, useEffect, useRef, Suspense, useState } from "react";
import { useLocation } from "react-router-dom";
import classNames from "classnames";
import styles from "styles/components/appshell.module.css";
import DrawerRenderer from "components/drawers/DrawerRenderer";
import ModalRenderer from "components/modals/ModalRenderer";
import appEventInstance from "util/eventHandler";
import { AppEventTypes } from "core/enums";
import Typography from "components/Typography";
import useDisableZoom from "hooks/useDisableZoom";
import useGameStore from "store/gameStore";
import { shallow } from "zustand/shallow";
import { getContentV2 } from "i18n/i18nHelper";
import { translationKeys } from "i18n/translationKeys.enums";
import Loader from "./Loader";

type AppShellProps = PropsWithChildren<HTMLAttributes<HTMLDivElement>>;

const AppShell: React.FC<AppShellProps> = ({ children, ...props }) => {
  const location = useLocation();
  const childrenRef = useRef<HTMLDivElement>(null);
  useDisableZoom(); // Disable zoom for all devices
  const [viewWidth, setViewWidth] = useState("0");

  const { isInitDataAvailable, setGlobalGameWidth } = useGameStore(
    (state) => ({
      isInitDataAvailable: state.isInitDataAvailable,
      setGlobalGameWidth: state.setGlobalGameWidth,
    }),
    shallow,
  );

  useEffect(() => {
    function convert44vhToVw() {
      // This function converts 44vh to vw to match wraper with the canvas dimensions
      const vhInPixels = (window.innerHeight < 780 ? 0.55 : 0.45) * window.innerHeight; // calculate vh like 45vh and 55vh
      const vwEquivalent = (vhInPixels / window.innerWidth) * 100;
      return vwEquivalent;
    }

    const setViewportHeight = () => {
      // console.log("converted value : ", convert44vhToVw().toFixed());
      let convertedWidth = convert44vhToVw().toFixed();
      setViewWidth(convertedWidth);
      setGlobalGameWidth(convertedWidth);
      const viewportHeight = window.innerHeight;
      document.documentElement.style.setProperty("--vh", `${viewportHeight * 0.01}px`);
    };

    // Initial setting of the --vh custom property
    setViewportHeight();

    // Recalculate the --vh value on window resize
    window.addEventListener("resize", setViewportHeight);

    const handleDrawerScrollToggle = (event: CustomEvent) => {
      const { detail } = event;
      const shouldDisableScroll = detail?.isScrollDisabled ?? false;

      if (childrenRef.current) {
        childrenRef.current.style.overflowY = shouldDisableScroll ? "hidden" : "auto";
      }
    };

    appEventInstance.listen(AppEventTypes.ToggleDrawerScroll, handleDrawerScrollToggle);

    return () => {
      appEventInstance.remove(AppEventTypes.ToggleDrawerScroll, handleDrawerScrollToggle);
      window.removeEventListener("resize", setViewportHeight);
    };
  }, []);

  let LazyLoadDrawerRenderer = React.lazy(() => import("components/drawers/DrawerRenderer"));
  let LazyLoadModalRenderer = React.lazy(() => import("components/modals/ModalRenderer"));

  const isMobile = window.innerWidth < 786;

  const style = {
    width: isMobile ? "100%" : `${viewWidth}vw`,
  };

  useEffect(() => {
    const setViewportHeight = () => {
      const vh = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty("--vh", `${vh * 0.01}px`);
    };

    setViewportHeight();
    window.addEventListener("resize", setViewportHeight);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", setViewportHeight);
    }

    return () => {
      window.removeEventListener("resize", setViewportHeight);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", setViewportHeight);
      }
    };
  }, []);


  return (
    <div className={classNames(styles.mainContainer)} {...props}>
      {/* if the overlapping solution doesnot work then add the style={style} to childContainer*/}
      <div className={classNames(styles.childContainer)} style={style}>
        {/* Hide message for /playcheck route */}
        {location.pathname !== "/playcheck" && (
          <div className={classNames(styles.notify)}>
            <Typography>{getContentV2(translationKeys.RESOLUTION_MSG)}</Typography>
          </div>
        )}

        <div className={classNames(styles.children)} ref={childrenRef}>
          {children}
        </div>

        <Suspense
          fallback={
            <section className="h-full">
              <Loader />
            </section>
          }
        >
          {isInitDataAvailable && (
            <>
              {/* <ForceSuspense shouldSuspend={false}> */}
              <LazyLoadDrawerRenderer />
              <LazyLoadModalRenderer />
              {/* </ForceSuspense> */}
            </>
          )}
        </Suspense>
      </div>
    </div>
  );
};
const ForceSuspense = ({ shouldSuspend, children }: any) => {
  if (shouldSuspend) {
    // This makes React suspend and show fallback
    throw new Promise((resolve) => setTimeout(resolve, 1000));
  }
  return children;
};

export default AppShell;
